import { useTranslation } from '@hooks/useTranslation'
import parse from 'html-react-parser'
import { ClipboardEvent, FC, FocusEvent, KeyboardEvent, useRef, useState } from 'react'
import s from './checkEmail.module.scss'
import { Button } from '@components/UIKit/Button/Button'
import cn from 'classnames'
import { useShowToast } from '@hooks/useShowToast'
import { CodeInputNew } from '@components/UIKit/CodeInput/CodeInputNew'

interface CheckEmailProps {
    sendMailHandler: () => Promise<void>
    sendCodeConfirm: (code: string) => Promise<void>
}

const defaultCode = ['', '', '', '']

export const CheckEmail: FC<CheckEmailProps> = ({ sendMailHandler, sendCodeConfirm }) => {
    const { showToast } = useShowToast()
    const { translate: tr } = useTranslation()
    const [code, setCode] = useState(defaultCode)
    const [buttonConfirmLoading, setButtonConfirmLoading] = useState(false)
    const inputsRef = useRef<Array<HTMLInputElement | null>>([])
    const buttonRef = useRef<HTMLButtonElement>(null)


    const handleChange = (value: string, index: number) => {
        if (!/^\d$/.test(value) && value !== '') return

        const newCode = [...code]
        newCode[index] = value
        setCode(newCode)

        // Перемещаем фокус на следующее поле при вводе цифры
        if (value && index < inputsRef.current.length - 1) {
            const inputRef = inputsRef.current[index + 1]
            inputRef?.focus()
        }

        // Перемещаем фокус на кнопку. без setTimeout не сработает фокус на кнопку из-за disabled
        if (index === inputsRef.current.length - 1 && !newCode.some(digi => !digi)) {
            setTimeout(() => { buttonRef.current?.focus() }, 0)
        }
    }

    const handleSubmit = async () => {
        if (isDisabled) return
        setButtonConfirmLoading(true)
        await sendCodeConfirm(code.join(''))
        setButtonConfirmLoading(false)
    }

    const resendHandler = async () => {
        await sendMailHandler()
        showToast({ variant: 'success' })
    }

    const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>, index: number) => {
        // Переход на предыдущий input при нажатии Backspace, если текущий input пустой
        if (e.key === 'Backspace' && code[index] === '' && index > 0) {
            const inputRef = inputsRef.current[index - 1]
            inputRef?.focus()
        }
    }

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => { e.target.select() }

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        const pasteData = e.clipboardData.getData('Text')

        // Проверяем, содержит ли вставленный текст только цифры и имеет ли длину, равную или меньше 4
        if (/^\d{4}$/.test(pasteData)) {
            const newCode = pasteData.split('').slice(0, 4) // Разбиваем на массив и берем максимум 4 символа
            setCode(newCode)
            // Обновляем значение в каждом input и фокусируемся на последнем заполненном
            newCode.forEach((char, index) => {
                if (!inputsRef.current[index]) return
                inputsRef.current[index].value = char
            })

            // Устанавливаем фокус на следующий незаполненный input
            const nextIndex = newCode.length < 4 ? newCode.length : 3
            inputsRef.current[nextIndex]?.focus()
        }
    }
    const isDisabled = code.some(digi => !digi)


    return (
        <form className={s.checkEmail}>
            <div className={s.checkEmail__head}>
                <h2 className={cn(
                    s.checkEmail__title,
                    '_title',
                )}>{parse(tr({ id: 'auth.page.code.title' }))}</h2>
                <div className={s.checkEmail__label}>{tr({ id: 'auth.page.code.text' })}</div>
            </div>
            <div className={s.checkEmail__form} onKeyDown={async e => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                await handleSubmit()
            }}>
                <div className={s.checkEmail__code_wrapper}>
                    {code.map((digit, index) => (
                        <CodeInputNew
                            key={index}
                            inputRef={ref => { inputsRef.current[index] = ref }}
                            type='string'
                            inputMode='numeric'
                            autoComplete='one-time-code'
                            maxLength={1}
                            value={digit}
                            onChange={e => handleChange(e.target.value, index)}
                            onKeyDown={async e => { await handleKeyDown(e, index) }}
                            onFocus={handleFocus}
                            onPaste={handlePaste}
                            min={0}
                        />
                    ))}
                </div>
                <Button
                    id='check_email_confirm_button'
                    color={'Black'}
                    size={'large'}
                    disabled={isDisabled}
                    loading={buttonConfirmLoading}
                    onClick={handleSubmit}
                    buttonRef={buttonRef}
                >
                    {tr({ id: 'btn.confirm' })}
                </Button>
            </div>
            <Button
                id='check_email_resend_button'
                className={s.checkEmail__resend}
                color={'Transparent'}
                size={'medium'}
                onClick={resendHandler}
            >
                {tr({ id: 'auth.page.code.resend' })}
            </Button>
        </form>
    )
}
